import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { enqueueSnackbar } from 'notistack';
import ComplianceChecklist from 'views/App/Portal/AWB/_components/ComplianceChecklist';
import useComplianceItemsLexipol from 'views/App/Portal/AWB/_data_sources/ComplianceItemsLexipol/useComplianceItemsLexipol';

export interface ComplianceCriteriaTabContentProps {
  standardId: string;
  allowDeleteComplianceItems: boolean;
}

const ComplianceCriteriaTabContent: React.FC<ComplianceCriteriaTabContentProps> = ({
  standardId,
  allowDeleteComplianceItems,
}) => {
  const { id: accreditationId, versionId } = useParams();
  const { isFeatureEnabled } = useContext(AppContext);

  const {
    data: items = [],
    isLoading,
    createItemAsync,
    updateItemAsync,
    deleteItemAsync,
    reorderAsync,
    isCreatingItem,
    isUpdatingItem,
    isDeletingItem,
    isReordering,
  } = useComplianceItemsLexipol(
    accreditationId ?? '',
    versionId ?? '',
    standardId,
    {},
    (err) => enqueueSnackbar(err?.message ?? 'Error', { variant: 'error' })
  );

  if (!isFeatureEnabled('awbPremiumLexipol') || !accreditationId || !versionId) {
    return null;
  }

  return (
    <ComplianceChecklist
      items={items}
      isLoading={isLoading}
      isCreatingItem={isCreatingItem}
      isUpdatingItem={isUpdatingItem}
      isDeletingItem={isDeletingItem}
      isReordering={isReordering}
      deleteDisabled={!allowDeleteComplianceItems}
      deleteDisabledTooltip={t('awb.complianceChecklist.lexipolTemplate.deleteBlockedPublishedVersion')}
      onSaveEdit={(id, html) =>
        updateItemAsync({ complianceItemId: id, updates: { draftContent: html } }).then(() =>
          enqueueSnackbar(t('awb.complianceChecklist.lexipolTemplate.snackbar.itemUpdated'), { variant: 'success' })
        )
      }
      onToggleEnabled={(id, enabled) =>
        updateItemAsync({ complianceItemId: id, updates: { enabled } }).then(() =>
          enqueueSnackbar(t('awb.complianceChecklist.lexipolTemplate.snackbar.itemEnabledToggled'), {
            variant: 'success',
          })
        )
      }
      onSaveNewItem={(html, position) =>
        createItemAsync({ position, draftContent: html }).then(() =>
          enqueueSnackbar(t('awb.complianceChecklist.lexipolTemplate.snackbar.itemAdded'), { variant: 'success' })
        )
      }
      onDelete={(id) =>
        deleteItemAsync({ complianceItemId: id }).then(() =>
          enqueueSnackbar(t('awb.complianceChecklist.lexipolTemplate.snackbar.itemDeleted'), { variant: 'success' })
        )
      }
      onReorder={(reorderedItems) =>
        reorderAsync({ items: reorderedItems }).then(() =>
          enqueueSnackbar(t('awb.complianceChecklist.lexipolTemplate.snackbar.orderUpdated'), { variant: 'success' })
        )
      }
    />
  );
};

export default ComplianceCriteriaTabContent;
